/**
 * KONSERTENE — personens egne, på tvers av band og byråer (sak 216, 0380).
 *
 * Konserten er hovedperspektivet (Atle, 24. september): «Begge byråene er synlige, men artisten ser
 * primært konsertene.» Tre former kommer ut av den samme lista:
 *   `performance`    en programpost med delt advance — festivalens tider, sted og advance
 *   `enquiry`        en Tvitvi-booking før advancen er delt — byråets dato, arrangør og sted
 *   `agency_booking` byråets egen booking, arrangør utenfor Tvitvi
 *
 * INGEN PENGER, INGEN VILKÅR, INGEN KONTRAKT I DOKUMENTET (216.6–216.8). Feltene finnes ikke i
 * `concertDetailSchema`, og databasefunksjonene som fyller den har en test som nekter kolonnene.
 * Kontrakt og økonomi har sin egen port og sin egen kontrakt, `concertDealSchema` (0453), og kommer
 * bare når byrået har slått dem på i Rettigheter (sak 229.8–229.10).
 *
 * HVA ARTISTEN SER STYRES AV BYRÅET (0453). Hvert område i `ARTIST_VIEW_AREAS` som er slått av, er
 * UTELATT fra dokumentet — nøkkelen finnes ikke, den er ikke `[]`. `areas` sier hvordan byrået har
 * satt bryterne, så siden kan skille «av» fra «tomt». Hodet (dato, sted, adresse, by, status,
 * arrangør, agent og management) er alltid med.
 */
import { z } from 'zod';

import { artistViewAreasSchema } from './artist-view.js';
import { engagementRefDtoSchema } from './engagements.js';
import { festivalContactSchema } from './info.js';
import { personStandardFieldsPatchSchema } from './person-card.js';
import {
  productionDeviationSchema,
  productionRiderSchema,
  productionRoomSpecSchema,
} from './production-advance.js';

export const CONCERT_KINDS = ['performance', 'enquiry', 'agency_booking'] as const;
export type ConcertKind = (typeof CONCERT_KINDS)[number];
export const concertKindSchema = z.enum(CONCERT_KINDS);

const dateOnly = z.iso.date();
/** Et øyeblikk. Postgres skriver det med forskyvning (`+02:00`) inne i et jsonb-dokument, så den tillates. */
const instant = z.iso.datetime({ offset: true });

export const concertSummarySchema = z
  .object({
    kind: concertKindSchema,
    id: z.uuid(),
    agencyId: z.uuid(),
    agencyName: z.string(),
    artistName: z.string(),
    formatName: z.string().nullable(),
    startsOn: dateOnly.nullable(),
    endsOn: dateOnly.nullable(),
    /** Bare for en programpost: festivalens klokkeslett, i `timeZone`. */
    startsAt: instant.nullable(),
    endsAt: instant.nullable(),
    timeZone: z.string().nullable(),
    organiserName: z.string().nullable(),
    venue: z.string().nullable(),
    festivalName: z.string().nullable(),
    editionName: z.string().nullable(),
    stageName: z.string().nullable(),
    city: z.string().nullable(),
    status: z.string().nullable(),
    /**
     * Bookingsaken (0927), lest gjennom `app.my_concert_engagements()` (0930): det samme statusordet
     * som Forespørsler, Konserter og bookinglista viser (QA13 102). Null når raden ikke har en.
     */
    engagement: engagementRefDtoSchema.nullable().optional(),
    /**
     * Artistens bilde (QA13 106), lest gjennom engasjementet (`app.my_concert_engagement_images()`, 0942): byråets
     * artist først, ellers arrangørens. Null uten sak eller uten bilde.
     */
    imageUrl: z.string().nullable().optional(),
  })
  .meta({ id: 'ConcertSummary' });
export type ConcertSummary = z.infer<typeof concertSummarySchema>;

export const concertListSchema = z
  .object({ items: z.array(concertSummarySchema) })
  .meta({ id: 'ConcertList' });
export type ConcertList = z.infer<typeof concertListSchema>;

const contactLine = z.object({
  name: z.string(),
  role: z.string().nullable().optional(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  /* Byråets reisefølge før advancen er delt (0398): alle på konserten ser allergi og kosthold. */
  allergy: z.string().nullable().optional(),
  diet: z.string().nullable().optional(),
  /* Fra personen (grep A1, 0915), som tekst siden A4 (1026). `allergy`/`diet` sendes ikke lenger. */
  food: z.string().nullable().optional(),
});

/**
 * En fil artisten kan laste ned gjennom `GET /v1/me/concerts/:kind/:id/files/:fileId` (0453). `id` er
 * vedleggets id — den samme som ruta tar.
 */
export const concertFileSchema = z
  .object({ id: z.uuid(), filename: z.string(), createdAt: instant })
  .meta({ id: 'ConcertFile' });
export type ConcertFile = z.infer<typeof concertFileSchema>;

/**
 * Byråets dokumenter og lenker på artisten (`booking_artist_links`, aldri avtaledokumentet). En
 * oppføring er en lenke, en fil eller begge; `fileId` er vedlegget ruta over laster ned.
 */
export const concertDocumentSchema = z
  .object({
    id: z.uuid(),
    kind: z.string(),
    label: z.string().nullable(),
    url: z.string().nullable(),
    fileId: z.uuid().nullable(),
    filename: z.string().nullable(),
    createdAt: instant,
  })
  .meta({ id: 'ConcertDocument' });
export type ConcertDocument = z.infer<typeof concertDocumentSchema>;

/**
 * Byråets interne samtaler om denne bookingen der personen har et sete (0369). `houseId` er huset
 * setet står i — det Innboks må stå i for å åpne samtalen.
 */
export const concertThreadSchema = z
  .object({
    id: z.uuid(),
    title: z.string().nullable(),
    houseId: z.uuid(),
    lastMessageAt: instant.nullable(),
  })
  .meta({ id: 'ConcertThread' });
export type ConcertThread = z.infer<typeof concertThreadSchema>;

/**
 * DØRA INN I DEN ENE ADVANCEN (svarene på 283–293, Del 1 B.1; 1140–1141). Mine konserter har ingen egen
 * side lenger: konserten åpnes der den bor, beskåret etter tilgangen.
 *   `guest`         gjest i arrangørens organisasjon, på programposten, med eget nivå (`permission`)
 *   `member`        medlem der programposten bor (byråets egen konsert eller bærer, festivalens folk)
 *   `agency`        medlem i byrået: byråets inngang til festivalens programpost (`advanceId`)
 *   `artist`        en forespørsel før advancen, for en mottaker: Delte artister › Bookinger
 *   `agency_artist` det samme for den som er medlem i byrået: byråets artistside
 */
export const CONCERT_DOOR_MODES = ['guest', 'member', 'agency', 'artist', 'agency_artist'] as const;
export const concertDoorSchema = z
  .object({
    mode: z.enum(CONCERT_DOOR_MODES),
    organizationId: z.uuid(),
    festivalId: z.uuid().nullable(),
    editionId: z.uuid().nullable(),
    performanceId: z.uuid().nullable(),
    permission: z.enum(['view', 'edit']).nullable(),
    artistId: z.uuid().nullable(),
    advanceId: z.uuid().nullable(),
  })
  .meta({ id: 'ConcertDoor' });
export type ConcertDoor = z.infer<typeof concertDoorSchema>;

export const concertDetailSchema = z
  .object({
    kind: concertKindSchema,
    id: z.uuid(),
    /**
     * Byråets brytere for denne konserten (0452), alle fjorten. Et område som er av, er utelatt fra
     * dokumentet; dette kartet sier at det er av og ikke tomt. To byråer: konserten følger byrået
     * den er gjennom.
     */
    areas: artistViewAreasSchema,
    agencyName: z.string(),
    artistName: z.string().nullable(),
    agent: contactLine.nullable(),
    management: z.array(contactLine),
    formatName: z.string().nullable().optional(),
    startsOn: dateOnly.nullable().optional(),
    endsOn: dateOnly.nullable().optional(),
    startsAt: instant.nullable().optional(),
    endsAt: instant.nullable().optional(),
    timeZone: z.string().nullable().optional(),
    organiserName: z.string().nullable().optional(),
    venue: z.string().nullable().optional(),
    festivalName: z.string().nullable().optional(),
    editionName: z.string().nullable().optional(),
    stageName: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    /**
     * STEDET OG KVELDEN FRA KONSERTEN (0420): for en programpost som hører til en konsert —
     * arrangørens med delt advance, eller byråets egen. Ingen av dem har penger i seg.
     */
    concertId: z.uuid().nullable().optional(),
    venueName: z.string().nullable().optional(),
    roomName: z.string().nullable().optional(),
    address: z.string().nullable().optional(),
    getInAt: instant.nullable().optional(),
    doorsAt: instant.nullable().optional(),
    curfewAt: instant.nullable().optional(),
    /** Konsertens kjøreplan (`concert_schedule_items`). */
    schedule: z
      .array(
        z.object({
          title: z.string(),
          startsAt: instant,
          endsAt: instant.nullable(),
          note: z.string().nullable(),
        }),
      )
      .optional(),
    /** Byråets reisefølge før advancen er delt. */
    party: z.array(contactLine).optional(),
    timetable: z
      .array(
        z.object({
          title: z.string(),
          startsAt: instant.nullable(),
          endsAt: instant.nullable(),
          location: z.string().nullable(),
        }),
      )
      .optional(),
    /**
     * «KONTAKTPERSONER HOS FESTIVALEN» (punkt 109, K4 F17): de samme radene festivalen og byrået ser
     * på programposten, med grunnene. Bare for en programpost, og bare når Personer er på.
     */
    festivalContacts: z.array(festivalContactSchema).optional(),
    /** Advancens Personer-fane. Allergi og kosthold bare for en selv, eller for management. */
    people: z
      .array(
        z.object({
          name: z.string(),
          role: z.string().nullable(),
          party: z.string(),
          phone: z.string().nullable(),
          email: z.string().nullable(),
          isMe: z.boolean(),
          allergy: z.string().nullable().optional(),
          diet: z.string().nullable().optional(),
          /* Fra personen (grep A1, 0915), som tekst (A4, 1026). */
          food: z.string().nullable().optional(),
        }),
      )
      .optional(),
    travel: z
      .array(
        z.object({
          kind: z.string().nullable(),
          mode: z.string().nullable(),
          from: z.string().nullable(),
          to: z.string().nullable(),
          departsAt: instant.nullable(),
          arrivesAt: instant.nullable(),
          /** 215.15 (0392): dagen alene, uten klokke. Valgfri for eldre svar. */
          departsDateOnly: z.boolean().optional(),
          arrivesDateOnly: z.boolean().optional(),
          reference: z.string().nullable(),
          vehicle: z.string().nullable(),
          driver: z.object({ name: z.string(), phone: z.string().nullable() }).nullable(),
          passengers: z.array(z.string()),
          mine: z.boolean(),
        }),
      )
      .optional(),
    stays: z
      .array(
        z.object({
          name: z.string().nullable(),
          address: z.string().nullable(),
          contactName: z.string().nullable(),
          contactPhone: z.string().nullable(),
          checkIn: z.string().nullable(),
          checkOut: z.string().nullable(),
          websiteUrl: z.string().nullable(),
          rooms: z.array(
            z.object({
              number: z.string().nullable(),
              type: z.string().nullable(),
              from: z.string().nullable(),
              to: z.string().nullable(),
              occupants: z.array(z.string()),
            }),
          ),
        }),
      )
      .optional(),
    /**
     * «KAN ENDRE» MED «ADVANCE» (276.28 b, 1060): personen kan føre leveranser og lenker i
     * seksjonene under. Valgfri for eldre svar; utelatt er nei.
     */
    canEditAdvance: z.boolean().optional(),
    sections: z
      .array(
        z.object({
          /** 1060: id-ene skrivingen trenger. Valgfrie for eldre svar. */
          id: z.uuid().optional(),
          kind: z.string().nullable(),
          name: z.string(),
          links: z.array(
            z.object({
              id: z.uuid().optional(),
              label: z.string().nullable(),
              url: z.string().nullable(),
            }),
          ),
          deliveries: z.array(
            z.object({
              id: z.uuid().optional(),
              name: z.string(),
              specification: z.string().nullable(),
              status: z.string().nullable(),
            }),
          ),
          dinners: z.array(
            z.object({
              startsAt: instant.nullable(),
              /** 215.15 (0392): dagen alene, uten klokke. */
              dateOnly: z.boolean().optional(),
              location: z.string().nullable(),
              pax: z.number().nullable(),
            }),
          ),
          /**
           * Filene på seksjonen (ridere, stage plot, promomateriell), 0453. Hver seksjon er med bare
           * når området den hører til er på (`advanceAreaForSection`).
           */
          files: z.array(concertFileSchema).optional(),
        }),
      )
      .optional(),
    deviations: z
      .array(
        z.object({
          title: z.string(),
          detail: z.string().nullable(),
          agreedWith: z.string().nullable(),
          agreedOn: z.string().nullable(),
        }),
      )
      .optional(),
    /**
     * SALEN, fra huset (`app.room_spec_doc`, 0418) — området `advance_technical`. Null når huset ikke
     * har bekreftet salen, eller stedet ikke er i Tvitvi. Aldri leie. Salens filer (`files[].id`)
     * lastes ned gjennom den samme fil-ruta.
     */
    room: productionRoomSpecSchema.nullable().optional(),
    /** Teknisk rider (Teknisk-seksjonen: leveranser, lenker, dokumenter) — `advance_technical`. */
    rider: productionRiderSchema.optional(),
    /**
     * Avvik og avtaler i produksjonsadvancen (0444) på denne acten og dem som gjelder hele konserten,
     * aldri en annen artists — området `deviations`, som `deviations` over.
     */
    productionDeviations: z.array(productionDeviationSchema).optional(),
    /** Byråets dokumenter og lenker på artisten — området `documents`. */
    documents: z.array(concertDocumentSchema).optional(),
    /** Byråets interne samtaler om bookingen der personen sitter — området `chat`. */
    threads: z.array(concertThreadSchema).optional(),
  })
  .meta({ id: 'ConcertDetail' });
export type ConcertDetail = z.infer<typeof concertDetailSchema>;

/**
 * SKRIVINGEN I ADVANCEN FRA MINE KONSERTER (276.28 b, 1060): leveranser og lenker i en seksjon, for
 * den som har «Kan endre» med «Advance». Lengdene er advancens egne (`MAX_ADVANCE_TEXT`/`_NOTE`).
 */
export const concertAdvanceDeliveryRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    specification: z.string().trim().max(2000).nullable().optional(),
  })
  .meta({ id: 'ConcertAdvanceDeliveryRequest' });
export type ConcertAdvanceDeliveryRequest = z.infer<typeof concertAdvanceDeliveryRequestSchema>;

export const concertAdvanceLinkRequestSchema = z
  .object({
    label: z.string().trim().min(1).max(200),
    url: z
      .string()
      .trim()
      .max(2000)
      .regex(/^https?:\/\//i),
  })
  .meta({ id: 'ConcertAdvanceLinkRequest' });
export type ConcertAdvanceLinkRequest = z.infer<typeof concertAdvanceLinkRequestSchema>;

/**
 * KONTRAKT OG ØKONOMI (`GET /v1/me/concerts/:kind/:id/deal`, `app.my_concert_deal`, 0453).
 *
 * Egen port og egen kontrakt, så konsertdokumentet aldri bærer en pengekolonne (RLS-spec 45 leser
 * kroppen til `my_concert`). Hver halvdel er null til byrået har slått på området sitt i Rettigheter
 * — standard er av for begge.
 *
 * `contract`: vilkårene og status, og kontraktsdokumentene (231.2: den signerte PDF-en når Kontrakt
 * er på). For en programpost er det arrangørens kontrakt slik byrået leser den i den delte advancen;
 * for byråets egen booking er det byråets vilkårstekst.
 * `economy`: honoraret og hva artisten dekker selv (0440).
 */
const dateOnlyValue = z.iso.date();

export const concertDealContractSchema = z.object({
  /** `signed` når gjeldende kontraktsdokument har en signeringsdato, `unsigned` ellers; null uten kontrakt. */
  status: z.enum(['signed', 'unsigned']).nullable(),
  /** Avtaletypen slik kontrakten fører den (`fee_kind`), fritekst. */
  dealType: z.string().nullable(),
  setCount: z.number().int().nullable(),
  setLengthMinutes: z.number().int().nullable(),
  capacity: z.number().int().nullable(),
  guestListSeats: z.number().int().nullable(),
  billing: z.string().nullable(),
  exclusivityText: z.string().nullable(),
  exclusivityRadiusKm: z.number().int().nullable(),
  exclusivityFrom: dateOnlyValue.nullable(),
  exclusivityTo: dateOnlyValue.nullable(),
  cancellationTerms: z.string().nullable(),
  recordingTerms: z.string().nullable(),
  merchSplit: z.string().nullable(),
  promoterCovers: z.string().nullable(),
  /** Vilkårsteksten: kontraktens vilkårspunkt, eller byråets egen tekst på bookingen. */
  termsText: z.string().nullable(),
  /** Tilleggsvilkårene (`contract_terms`), med husets egne navn. */
  extraTerms: z.array(z.object({ label: z.string(), value: z.string().nullable() })),
  /** Kontraktsdokumentene, nyeste versjon først. `id` er vedlegget fil-ruta laster ned. */
  documents: z.array(
    z.object({
      id: z.uuid(),
      filename: z.string(),
      versionNumber: z.number().int(),
      signedOn: dateOnlyValue.nullable(),
      isCurrent: z.boolean(),
    }),
  ),
});
export type ConcertDealContract = z.infer<typeof concertDealContractSchema>;

export const concertDealEconomySchema = z.object({
  /** Honoraret i minste enhet (øre). Null når det ikke er satt. */
  feeMinor: z.number().int().nullable(),
  currency: z.string().nullable(),
  /** «Artist dekker» (0440): reise, overnatting og mat inngår i honoraret. */
  artistCovers: z.object({
    travel: z.boolean(),
    accommodation: z.boolean(),
    food: z.boolean(),
  }),
});
export type ConcertDealEconomy = z.infer<typeof concertDealEconomySchema>;

export const concertDealSchema = z
  .object({
    contract: concertDealContractSchema.nullable(),
    economy: concertDealEconomySchema.nullable(),
  })
  .meta({ id: 'ConcertDeal' });
export type ConcertDeal = z.infer<typeof concertDealSchema>;

/**
 * DET ARTISTEN TAR MED SEG (sak 216, 0384): personens egne opplysninger, som flyter ut til bandradene
 * hos byråene hen har godtatt og til kommende konserter med delt advance. Tom streng er «ikke satt».
 */
const detail = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === null || value === '' ? null : value));

export const personDetailsSchema = z
  .object({
    instrument: detail(120),
    basedIn: detail(120),
    /* Standardfeltene (grep A1, 0914), som tekst siden A4 (1026). 0384-feltene (dietary,
       allergies) står i `food`, fylt av 1026. */
    tshirtSize: z.string().nullable(),
    drivingLicence: z.string().nullable(),
    dateOfBirth: z.string().nullable(),
    food: z.string().nullable(),
    emergencyName: z.string().nullable(),
    emergencyPhone: z.string().nullable(),
    emergencyRelation: z.string().nullable(),
  })
  .meta({ id: 'PersonDetails' });
export type PersonDetails = z.infer<typeof personDetailsSchema>;

/**
 * `PUT /v1/me/details` — feltene som er med, skrives; de som er utelatt, står (grep A1). Tom streng
 * eller tom liste tømmer feltet.
 */
export const savePersonDetailsRequestSchema = personStandardFieldsPatchSchema.meta({
  id: 'SavePersonDetailsRequest',
});
export type SavePersonDetailsRequest = z.infer<typeof savePersonDetailsRequestSchema>;
